import {
    CollectionReference,
    deleteDoc,
    doc,
    getDocs,
    onSnapshot,
    Query,
    query,
    serverTimestamp,
    setDoc,
    type Unsubscribe,
    updateDoc,
    where,
} from "firebase/firestore";
import type { Path } from "react-hook-form";

export type Callback<T> = (data: T[]) => void;

export interface ListenerEntry<T> {
    subscribers: Set<Callback<T>>;
    cache: T[];
    unsub?: () => void;
}

export abstract class DatabaseRepository<T extends { uid: string }> {
    protected abstract collection: CollectionReference<T>;
    protected listeners = new Map<string, ListenerEntry<T>>();

    abstract getById(uid: string): Promise<T | null>;

    async add(data: Omit<T, "uid">) {
        try {
            const ref = doc(this.collection);
            await this.upsert({ ...data, createdAt: serverTimestamp(), uid: ref.id } as unknown as T);
            return;
        } catch (err) {
            console.error("Failed to add data:", err);
            throw err;
        }
    }

    async getByData(fieldPath: Path<T>, value: unknown) {
        try {
            const querySnapshot = await getDocs(query(this.collection, where(fieldPath, "==", value)));
            return querySnapshot.docs.map((doc) => doc.data() as T);
        } catch (err) {
            console.error("Failed to get data:", err);
            throw err;
        }
    }

    async getAll(): Promise<T[]> {
        try {
            const data: T[] = [];

            const querySnapshot = await getDocs(this.collection);
            querySnapshot.forEach((doc) => {
                data.push(doc.data() as T);
            });

            return data;
        } catch (err) {
            console.error("Failed to get all data:", err);
            throw err;
        }
    }

    async upsert(data: T): Promise<void> {
        const ref = doc(this.collection, data.uid);
        await setDoc(
            ref,
            {
                ...data,
                updatedAt: serverTimestamp(),
            },
            { merge: true },
        );
    }

    async updatePartial(uid: string, patch: Partial<Omit<T, "uid">>): Promise<void> {
        try {
            const ref = doc(this.collection, uid);
            await updateDoc(ref, { ...patch, updatedAt: serverTimestamp() });
            console.log("Update has been successful:", uid);
        } catch (err) {
            console.error("Failed to update data:", err);
            throw err;
        }
    }

    async delete(uid: string): Promise<void> {
        try {
            const ref = doc(this.collection, uid);
            await deleteDoc(ref);
        } catch (err) {
            console.error("Failed to delete data:", err);
            throw err;
        }
    }

    subscribe<K extends keyof T>(cb: Callback<T>, filters: Partial<Pick<T, K>> = {}): Unsubscribe {
        const key = JSON.stringify(filters);
        let entry = this.listeners.get(key);
        if (!entry) {
            entry = { subscribers: new Set(), cache: [] };
            this.listeners.set(key, entry);

            let q: Query<T> = this.collection;
            for (const k in filters) {
                if (filters[k] !== undefined) {
                    q = query(q, where(k as string, "==", filters[k]!)) as Query<T>;
                }
            }

            entry.unsub = onSnapshot(q, (snap) => {
                const data = snap.docs.map((doc) => doc.data() as T);
                entry!.cache = data;
                entry!.subscribers.forEach((cb) => cb(data));
            });
        }

        entry.subscribers.add(cb);
        if (entry.cache.length) cb(entry.cache);

        return () => {
            entry!.subscribers.delete(cb);
            if (entry!.subscribers.size === 0) {
                entry!.unsub?.();
                this.listeners.delete(key);
            }
        };
    }
}
